import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { IListItem, IUserDetail } from 'react-scale/components/WebinarSessions/types';
import {
  GetLiveSessionResult,
  ICreateSessionAPIParams,
  GetCollectionResponse,
  GetRecordingResponse,
  EditCollectionInfoRequest,
  GetCollectionsSearchResponse,
  GetCollectionsSearchParams,
  EditCollectionInviteesRequest,
  EditCollectionSessionsRequest,
  AddCollectionSessionsRequest,
  LookupUsersResponse,
  LookupUsersRequest,
  GetCollectionSessionWithHydratedUsersResponse,
  GetCollectionRequestParams,
  WebinarMissionConfigResponse,
  GetCollectionSession,
} from '@scale/shared/webinarSessions/types';
import EnrichedError from 'react-scale/lib/enrichedError';
import { useQuery } from '@tanstack/react-query-v4';

// TODO: Migrate hooks to their own folder / files

export async function lookupUserIdsOrEmails(userIdsOrEmails: string[]): Promise<IUserDetail[]> {
  const url = `internal/webinar-sessions/lookup/users`;
  try {
    return await apiFetch<IUserDetail[]>(null, url, {
      method: 'POST',
      body: { userIdsOrEmails },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      "Couldn't get users from IDs or emails.",
      e.response?.message ?? e.toString(),
      `Error getting users from IDs or emails.`,
      url,
    );
  }
}

export function useLookupUserIdsOrEmails({
  userIdsOrEmails,
  enabled = true,
}: {
  userIdsOrEmails: string[];
  enabled?: boolean;
}) {
  return useQuery<IUserDetail[]>({
    queryKey: ['useLookupUserIdsOrEmails', userIdsOrEmails],
    queryFn: () => lookupUserIdsOrEmails(userIdsOrEmails),
    enabled,
  });
}

export async function lookupWorkerTeams(ids: string[]): Promise<IListItem[]> {
  const url = `internal/webinar-sessions/lookup/workerTeams`;
  try {
    return await apiFetch<IListItem[]>(null, url, {
      method: 'POST',
      body: { ids },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      "Couldn't get worker teams from IDs.",
      e.response?.message ?? e.toString(),
      `Error getting worker teams from IDs.`,
      url,
    );
  }
}

export function useLookupWorkerTeamsById({ workerTeamIds }: { workerTeamIds: string[] }) {
  return useQuery<IListItem[]>({
    queryKey: ['useLookupWorkerTeamsById', workerTeamIds],
    queryFn: () => lookupWorkerTeams(workerTeamIds),
  });
}

export async function lookupQualifications(ids: string[]): Promise<IListItem[]> {
  const url = `internal/webinar-sessions/lookup/qualifications`;
  try {
    return await apiFetch<IListItem[]>(null, url, {
      method: 'POST',
      body: { ids },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      "Couldn't get qualifications from IDs.",
      e.response?.message ?? e.toString(),
      `Error getting qualifications from IDs.`,
      url,
    );
  }
}

export function useLookupQualificationsById({ qualificationIds }: { qualificationIds: string[] }) {
  return useQuery<IListItem[]>({
    queryKey: ['useLookupQualificationsById', qualificationIds],
    queryFn: () => lookupQualifications(qualificationIds),
  });
}

export async function lookupSquads(ids: string[]): Promise<IListItem[]> {
  const url = `internal/webinar-sessions/lookup/squads`;
  try {
    return await apiFetch<IListItem[]>(null, url, {
      method: 'POST',
      body: { ids },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      "Couldn't get squads from IDs.",
      e.response?.message ?? e.toString(),
      `Error getting squads from IDs.`,
      url,
    );
  }
}

export function useLookupSquadsById({ squadIds }: { squadIds: string[] }) {
  return useQuery<IListItem[]>({
    queryKey: ['useLookupSquadsById', squadIds],
    queryFn: () => lookupSquads(squadIds),
  });
}

export function useSearchUsers(params: LookupUsersRequest) {
  return useQuery<LookupUsersResponse>({
    queryKey: ['useSearchUsers', params],
    queryFn: () => searchUsers(params),
  });
}

async function searchUsers(request: LookupUsersRequest) {
  const url = `internal/webinar-sessions/users/search`;
  try {
    return await apiFetch<LookupUsersResponse>(null, url, {
      method: 'POST',
      body: request,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't search users.",
      error.response?.message ?? error.toString(),
      'Error searching users.',
      url,
    );
  }
}

export async function fetchUsersFromWT(workerTeamId: string): Promise<IUserDetail[]> {
  return await apiFetch<IUserDetail[]>(null, `internal/webinar-sessions/lookup/workerTeam`, {
    method: 'POST',
    body: { workerTeamId },
  });
}

export async function fetchProjectQualifications(projectId: string): Promise<IListItem[]> {
  const url = `internal/webinar-sessions/lookup/project-qualifications`;
  try {
    return await apiFetch<IListItem[]>(null, url, {
      method: 'POST',
      body: { projectId },
    });
  } catch (e) {
    throw new EnrichedError(
      e.status,
      `Error getting project qualifications.`,
      e.response?.message ?? e.toString(),
      `Error getting project qualifications.`,
      url,
    );
  }
}

export function useFetchProjectQualifications({ projectId }: { projectId: string }) {
  return useQuery<IListItem[]>({
    queryKey: ['useFetchProjectQualifications', projectId],
    queryFn: () => fetchProjectQualifications(projectId),
  });
}

export async function createNewSession(
  params: ICreateSessionAPIParams,
  communications: Record<string, boolean>,
): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/create-session`, {
      method: 'POST',
      body: { params, communications },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function createNewSessionCollection(
  params: ICreateSessionAPIParams,
  communications: Record<string, boolean>,
): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/create-sessions`, {
      method: 'POST',
      body: { params, communications },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function addSessionsToCollection(params: {
  collectionId: string;
  eventStart: string;
  duration: number;
  occurrences: string[];
}): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/add-sessions-to-collection`, {
      method: 'POST',
      body: { ...params },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function editSession(params: {
  sessionId: string;
  changedFields: any;
  applyToFutureSessions: boolean;
}): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/edit-session`, {
      method: 'POST',
      body: { ...params },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

async function getLiveSession(sessionId: string): Promise<GetLiveSessionResult> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/session/${sessionId}/live-session`, {
      method: 'GET',
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export function useGetLiveSession({
  sessionId,
  enabled = true,
}: {
  sessionId: string;
  enabled?: boolean;
}) {
  return useQuery<GetLiveSessionResult>({
    queryKey: ['useGetLiveSession', sessionId],
    queryFn: () => getLiveSession(sessionId),
    enabled,
  });
}

export async function endSession(sessionId: string): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/end-session`, {
      method: 'POST',
      body: { sessionId },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function getHostedCollections(projectId?: string): Promise<any> {
  if (projectId)
    return await apiFetch(null, `internal/webinar-sessions/hosted-collections/${projectId}`, {
      method: 'GET',
    });
  return await apiFetch(null, `internal/webinar-sessions/hosted-collections`, {
    method: 'GET',
  });
}

export async function getProjectCollections(projectId: string): Promise<any> {
  return await apiFetch(null, `internal/webinar-sessions/project-collections/${projectId}`, {
    method: 'GET',
  });
}

async function getSessionsForCollection<T extends boolean>({
  collectionId,
  hydrateUsers,
}: {
  collectionId: string;
  hydrateUsers: T;
}): Promise<
  T extends true ? GetCollectionSessionWithHydratedUsersResponse : GetCollectionResponse
> {
  const url = `internal/webinar-sessions/collection/${collectionId}`;
  const queryParams: GetCollectionRequestParams = {
    hydrateUsers,
  };
  try {
    return await apiFetch<
      T extends true ? GetCollectionSessionWithHydratedUsersResponse : GetCollectionResponse
    >(null, url, {
      method: 'GET',
      query: queryParams,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't get sessions for collection.",
      error.response?.message ?? error.toString(),
      'Error getting sessions for collection.',
      url,
    );
  }
}

export function useGetSessionsForCollection({ collectionId }: { collectionId: string }) {
  return useQuery<GetCollectionResponse>({
    queryKey: ['useGetSessionsForCollection', collectionId],
    queryFn: () => getSessionsForCollection({ collectionId, hydrateUsers: false }),
  });
}

export function useGetSessionsForCollectionWithHydratedUsers({
  collectionId,
}: {
  collectionId: string;
}) {
  return useQuery<GetCollectionSessionWithHydratedUsersResponse>({
    queryKey: ['useGetSessionsForCollectionWithHydratedUsers', collectionId],
    queryFn: () => getSessionsForCollection({ collectionId, hydrateUsers: true }),
  });
}

export async function searchCollections(
  params: GetCollectionsSearchParams,
): Promise<GetCollectionsSearchResponse> {
  return await apiFetch(null, `internal/webinar-sessions/collections/search`, {
    method: 'GET',
    query: params,
  });
}

export async function cancelSession(sessionId: string, reason: string): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/cancel-session`, {
      method: 'POST',
      body: { sessionId, cancelationReason: reason },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function collectAttendance(sessionId: string): Promise<any> {
  try {
    return await apiFetch(null, `internal/webinar-sessions/collect-attendance`, {
      method: 'POST',
      body: { sessionId },
    });
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return null;
  }
}

export async function collectRegistrants(sessionId: string): Promise<GetCollectionSession | null> {
  const url = `internal/webinar-sessions/collect-registrants`;
  try {
    return await apiFetch(null, url, {
      method: 'POST',
      body: { sessionId },
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't collect registrants.",
      error.response?.message ?? error.toString(),
      'Error collecting registrants.',
      url,
    );
  }
}

export async function fetchWebinarSessionRecording(
  sessionId: string,
): Promise<GetRecordingResponse> {
  return await apiFetch(null, `internal/webinar-sessions/session/${sessionId}/recording`, {
    method: 'GET',
  });
}

export async function deleteSession(sessionId: string): Promise<any> {
  return await apiFetch(null, `internal/webinar-sessions/session/${sessionId}`, {
    method: 'DELETE',
  });
}

export async function editCollectionInfo({
  collectionId,
  params,
}: {
  collectionId: string;
  params: EditCollectionInfoRequest;
}): Promise<void> {
  const url = `internal/webinar-sessions/collection/${collectionId}/edit-info`;
  try {
    await apiFetch(null, url, {
      method: 'POST',
      body: params,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't edit collection info.",
      error.response?.message ?? error.toString(),
      'Error editing collection info.',
      url,
    );
  }
}

export async function editCollectionInvitees({
  collectionId,
  params,
}: {
  collectionId: string;
  params: EditCollectionInviteesRequest;
}): Promise<void> {
  const url = `internal/webinar-sessions/collection/${collectionId}/edit-invitees`;
  try {
    await apiFetch(null, url, {
      method: 'POST',
      body: params,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't edit collection invitees.",
      error.response?.message ?? error.toString(),
      'Error editing collection invitees.',
      url,
    );
  }
}

export async function editCollectionSessions({
  collectionId,
  params,
}: {
  collectionId: string;
  params: EditCollectionSessionsRequest;
}): Promise<void> {
  const url = `internal/webinar-sessions/collection/${collectionId}/edit-existing-sessions`;
  try {
    await apiFetch(null, url, {
      method: 'POST',
      body: params,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't edit collection sessions.",
      error.response?.message ?? error.toString(),
      'Error editing collection sessions.',
      url,
    );
  }
}

export async function addSessionsToCollectionV2({
  collectionId,
  params,
}: {
  collectionId: string;
  params: AddCollectionSessionsRequest;
}): Promise<void> {
  const url = `internal/webinar-sessions/collection/${collectionId}/add-sessions`;
  try {
    await apiFetch(null, url, {
      method: 'POST',
      body: params,
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't add sessions to collection.",
      error.response?.message ?? error.toString(),
      'Error adding sessions to collection.',
      url,
    );
  }
}

async function getMissionConfigsForCollection(
  collectionId: string,
): Promise<WebinarMissionConfigResponse> {
  const url = `internal/webinar-sessions/collection/${collectionId}/mission-configs`;
  try {
    return await apiFetch(null, url, {
      method: 'GET',
    });
  } catch (error) {
    throw new EnrichedError(
      error.status,
      "Couldn't get mission configs for collection.",
      error.response?.message ?? error.toString(),
      'Error getting mission configs for collection.',
      url,
    );
  }
}

export function useGetMissionConfigsForCollection({ collectionId }: { collectionId: string }) {
  return useQuery<WebinarMissionConfigResponse>({
    queryKey: ['useGetMissionConfigsForCollection', collectionId],
    queryFn: () => getMissionConfigsForCollection(collectionId),
  });
}
